import React, { useState } from "react";
import {
  Building2,
  CheckCircle,
  Plus,
  ArrowLeft,
  AlertCircle,
  RefreshCw,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const OrganizationForm = () => {
  const [formData, setFormData] = useState({
    org_name: "",
    address: "",
    phone_number: "",
    GST_number: "",
  });
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const [responseMessage, setResponseMessage] = useState("");
  const [responseType, setResponseType] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const handleChange = (event) => {
    setFormData((prev) => ({
      ...prev,
      [event.target.name]: event.target.value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await fetch(`${BASE_URL}/orgdetails/addOrganization`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        console.error("Failed to add organization");
        setResponseMessage("Failed to add organization.");
        setResponseType("error");
      } else {
        await response.json();
        setResponseMessage("Organization added successfully!");
        setResponseType("success");
        setFormData({
          org_name: "",
          address: "",
          phone_number: "",
          GST_number: "",
        });
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setResponseMessage("Failed to add organization.");
      setResponseType("error");
    } finally {
      setIsSubmitting(false);
      setShowModal(true);
    }
  };

  const ResponseModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold text-gray-900">
            {responseType === "success" ? "Success!" : "Error"}
          </h3>
          <button
            onClick={() => setShowModal(false)}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        <div className="flex items-center justify-center mb-6">
          <div
            className={`rounded-full p-3 ${
              responseType === "success" ? "bg-green-100" : "bg-red-100"
            }`}
          >
            {responseType === "success" ? (
              <CheckCircle className="h-8 w-8 text-green-500" />
            ) : (
              <AlertCircle className="h-8 w-8 text-red-500" />
            )}
          </div>
        </div>
        <p className="text-center text-gray-600 mb-6">{responseMessage}</p>
        <button
          onClick={() => {
            setShowModal(false);
            if (responseType === "success") {
              navigate("/organizations");
            }
          }}
          className="w-full bg-blue-600 text-white rounded-lg py-3 hover:bg-blue-700 transition-colors"
        >
          {responseType === "success" ? "View All Organizations" : "Try Again"}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <div className="p-2 bg-blue-50 rounded-lg">
                <Building2 className="h-6 w-6 text-blue-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900">
                Add Organization
              </h1>
            </div>
            <button
              onClick={() => navigate("/organizations")}
              className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Return
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Organization Details
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Fill in the details to add a new organization.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                Organization Name
              </label>
              <input
                type="text"
                name="org_name"
                value={formData.org_name}
                onChange={handleChange}
                className="block w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500 focus:outline-none transition-colors sm:text-sm"
                placeholder="Enter organization name"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                Address
              </label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                className="block w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500 focus:outline-none transition-colors sm:text-sm"
                placeholder="Enter complete address"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                Phone Number
              </label>
              <input
                type="tel"
                name="phone_number"
                value={formData.phone_number}
                onChange={handleChange}
                className="block w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500 focus:outline-none transition-colors sm:text-sm"
                placeholder="Enter phone number"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                Organization GST Number
              </label>
              <input
                type="text"
                name="GST_number"
                value={formData.GST_number}
                onChange={handleChange}
                className="block w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500 focus:outline-none transition-colors sm:text-sm"
                placeholder="Enter organization GST number"
                required
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full flex justify-center items-center px-6 py-3 border border-transparent rounded-lg text-base font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? (
                <RefreshCw className="h-5 w-5 animate-spin" />
              ) : (
                <>
                  <Plus className="h-5 w-5 mr-2" />
                  Add Organization
                </>
              )}
            </button>
          </form>
        </div>
      </main>
      {showModal && <ResponseModal />}
    </div>
  );
};

export default OrganizationForm;
